import { Injectable, signal, effect } from '@angular/core';

export type Theme = 'light' | 'dark';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly STORAGE_KEY = 'callavoidance_theme';
  private readonly COOKIE_NAME = 'callavoidance_theme';
  private readonly COOKIE_DAYS = 365; // Persist for 1 year

  // Signal for reactive theme state
  readonly theme = signal<Theme>(this.getStoredTheme());

  constructor() {
    // Effect to apply theme changes to DOM and persist
    effect(() => {
      const currentTheme = this.theme();
      this.applyTheme(currentTheme);
      this.persistTheme(currentTheme);
    });
  }

  private getStoredTheme(): Theme {
    // Try localStorage first
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY) as Theme | null;
      if (stored === 'light' || stored === 'dark') {
        return stored;
      }
    } catch {
      // localStorage not available, try cookie
    }

    // Try cookie as fallback
    const cookieValue = this.getCookie(this.COOKIE_NAME);
    if (cookieValue === 'light' || cookieValue === 'dark') {
      return cookieValue;
    }

    // Check system preference as final fallback
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  }

  private persistTheme(theme: Theme): void {
    // Try localStorage first
    try {
      localStorage.setItem(this.STORAGE_KEY, theme);
    } catch {
      // localStorage not available
    }

    // Also set cookie as backup
    this.setCookie(this.COOKIE_NAME, theme, this.COOKIE_DAYS);
  }

  private setCookie(name: string, value: string, days: number): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }

  private getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return cookie.substring(nameEQ.length);
      }
    }
    return null;
  }

  private applyTheme(theme: Theme): void {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      root.setAttribute('data-theme', 'dark');
      root.style.colorScheme = 'dark';
    } else {
      root.classList.remove('dark');
      root.setAttribute('data-theme', 'light');
      root.style.colorScheme = 'light';
    }
  }

  toggleTheme(): void {
    this.theme.update(current => current === 'light' ? 'dark' : 'light');
  }

  setTheme(theme: Theme): void {
    this.theme.set(theme);
  }

  isDark(): boolean {
    return this.theme() === 'dark';
  }
}
